// AttendanceReports.js
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../../AuthContext';
import configData from '../../configure';
import { getFacultyAssignedClassNames, filterClassesForFaculty } from '../../utils/facultyHierarchyUtils';
import {
  getStoredAttendance,
  getStoredSettings,
  DEFAULT_CLASSES,
  DEFAULT_SUBJECTS,
  DEFAULT_STUDENTS,
  calculateStudentAttendanceStats,
  seedLiveRosterHistory
} from './attendanceSeed';
import { fetchAttendanceRecords, fetchAllAttendanceRecords } from '../../services/attendanceService';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './AttendanceReports.css';

// jsPDF Imports
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';

import AssessmentIcon from '@mui/icons-material/Assessment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import ClassIcon from '@mui/icons-material/Class';
import SchoolIcon from '@mui/icons-material/School';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import SearchIcon from '@mui/icons-material/Search';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';

const formatLocalDate = (dateVal) => {
  if (!dateVal) return '';
  const pad = (num) => num < 10 ? `0${num}` : `${num}`;
  if (Array.isArray(dateVal)) {
    const [year, month, day] = dateVal;
    return `${year}-${pad(month)}-${pad(day)}`;
  }
  if (typeof dateVal === 'object') {
    const year = dateVal.year;
    const month = dateVal.monthValue || dateVal.month;
    const day = dateVal.dayOfMonth || dateVal.day;
    if (year && month && day) {
      let m = month;
      if (typeof month === 'string') {
        const months = {
          JANUARY: 1, FEBRUARY: 2, MARCH: 3, APRIL: 4, MAY: 5, JUNE: 6,
          JULY: 7, AUGUST: 8, SEPTEMBER: 9, OCTOBER: 10, NOVEMBER: 11, DECEMBER: 12
        };
        m = months[month.toUpperCase()] || 1;
      }
      return `${year}-${pad(m)}-${pad(day)}`;
    }
  }
  if (typeof dateVal === 'string') {
    return dateVal.split('T')[0];
  }
  return dateVal.toString();
};

const mapStatusFromBackend = (status) => {
  switch (status) {
    case 'PRESENT': return 'Present';
    case 'ABSENT': return 'Absent';
    case 'LATE': return 'Late';
    case 'ON_LEAVE': return 'Leave';
    default: return status || 'Present';
  }
};

const AttendanceReports = ({ orgId }) => {
  const { axios, token, userRole, user } = useAuth();

  const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';

  // Filter States
  const [selectedCenter, setSelectedCenter] = useState('');
  const [selectedClassId, setSelectedClassId] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('ALL'); // 'ALL' or specific
  
  // Date range states: default is last 30 days
  const [startDate, setStartDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => new Date().toISOString().split('T')[0]);

  // Dropdowns lists
  const [classesList, setClassesList] = useState([]);
  const [subjectsList, setSubjectsList] = useState([]);
  const [students, setStudents] = useState([]);

  const uniqueCenters = useMemo(() => {
    if (isCustomOrg) {
      return [...new Set(classesList.map(c => c.label || c.name || c.className).filter(Boolean))];
    }
    return [...new Set(classesList.map(c => c.sectionName || c.label || c.name).filter(Boolean))];
  }, [classesList, isCustomOrg]);

  useEffect(() => {
    if (uniqueCenters.length > 0) {
      if (!selectedCenter || !uniqueCenters.includes(selectedCenter)) {
        setSelectedCenter(uniqueCenters[0]);
      }
    }
  }, [uniqueCenters, selectedCenter]);

  const matchingClasses = useMemo(() => {
    if (isCustomOrg) {
      return classesList.filter(c => (c.label || c.name || c.className) === selectedCenter);
    } else {
      const cls = classesList.find(c => c.classId === selectedClassId);
      return cls ? [cls] : [];
    }
  }, [isCustomOrg, classesList, selectedCenter, selectedClassId]);
  
  // Tab states
  const [activeTab, setActiveTab] = useState('defaulters'); // 'defaulters', 'subjectWise', 'studentSummary'
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [settings, setSettings] = useState(null);

  // Parent Notification sent tracking
  const [notifiedStudents, setNotifiedStudents] = useState({});
  const [dbRecords, setDbRecords] = useState([]);

  // Fetch class attendance records from backend API
  useEffect(() => {
    if (!token || matchingClasses.length === 0) return;
    
    const fetchClassRecords = async () => {
      setIsLoading(true);
      try {
        const promises = matchingClasses.map(async (cls) => {
          try {
            const result = await fetchAllAttendanceRecords({
              filterType: 'class',
              id: cls.classId,
              startDate: startDate,
              endDate: endDate
            });
            return result || [];
          } catch (e) {
            console.warn(`Failed API attendance fetch in Reports for class ${cls.classId}`, e);
            return [];
          }
        });
        const results = await Promise.all(promises);
        setDbRecords(results.flat());
      } catch (e) {
        console.warn("Failed API attendance fetch in Reports", e);
        setDbRecords([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchClassRecords();
  }, [matchingClasses, startDate, endDate, token]);

  // Initialize
  useEffect(() => {
    const initializePage = async () => {
      setIsLoading(true);
      const loadedSettings = getStoredSettings();
      setSettings(loadedSettings);

      try {
        let classes = [];
        let subjects = [];

        // Fetch live classes
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_CLASSES_BY_ORG}/${orgId}/metadata`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
            const rawClasses = res.data?.items || res.data || [];
            classes = rawClasses.map(c => {
              const centerName = isCustomOrg
                ? (c.className || c.label || c.name || '').trim()
                : (c.name || (c.sectionName && c.sectionName !== 'null' && c.sectionName !== 'undefined' ? `${c.className || ''}-${c.sectionName}` : c.className || '')).trim();
              return {
                classId: c.classId || c.id,
                value: c.classId || c.id,
                className: c.className || centerName,
                sectionName: isCustomOrg ? '' : (c.sectionName || ''),
                name: centerName || 'Unknown Center',
                label: centerName || 'Unknown Center'
              };
            });

            const assignedClassNames = await getFacultyAssignedClassNames(orgId, user, axios);
            classes = filterClassesForFaculty(classes, assignedClassNames);
          } catch (e) {
            console.warn("Failed API classes fetch in Reports, falling back to seed", e);
          }
        }

        const isCustomOrg = orgId === '62d3b1f4-e453-4933-bfcf-7b5adf5b4e83';
        if (!isCustomOrg && (!classes || classes.length === 0)) {
          classes = DEFAULT_CLASSES;
        }

        // Fetch live subjects
        if (orgId && token) {
          try {
            const res = await axios.get(`${configData.GET_SUBJECTS_BY_ORG}/${orgId}`, {
              headers: { Authorization: `Bearer ${token}` }
            });
            const rawSubjects = res.data?.items || res.data || [];
            subjects = rawSubjects.map(sub => ({
              courseCode: sub.courseCode || sub.id,
              courseName: sub.courseDetails?.courseName || sub.name || 'Unknown Subject',
              period: sub.period || 'General'
            }));
          } catch (e) {
            console.warn("Failed API subjects fetch in Reports, falling back to seed", e);
          }
        }

        if (!subjects || subjects.length === 0) {
          subjects = DEFAULT_SUBJECTS;
        }

        setClassesList(classes);
        setSubjectsList(subjects);

        const calculatedCenters = [...new Set(classes.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (classes.length > 0) setSelectedClassId(classes[0].classId);

      } catch (err) {
        console.error("Failed loading Reports page filters", err);
        setClassesList(DEFAULT_CLASSES);
        setSubjectsList(DEFAULT_SUBJECTS);
        const calculatedCenters = [...new Set(DEFAULT_CLASSES.map(c => c.sectionName).filter(Boolean))];
        if (calculatedCenters.length > 0) setSelectedCenter(calculatedCenters[0]);
        if (DEFAULT_CLASSES.length > 0) setSelectedClassId(DEFAULT_CLASSES[0].classId);
      } finally {
        setIsLoading(false);
      }
    };

    initializePage();
  }, [orgId, token, axios]);

  // Load roster
  useEffect(() => {
    if (matchingClasses.length === 0) return;
    
    const loadStudents = async () => {
      setIsLoading(true);
      try {
        const studentPromises = matchingClasses.map(async (cls) => {
          let studentList = [];
          if (token) {
            try {
              const res = await axios.get(`${configData.GET_STUDENTS_BY_CLASS}/${cls.classId}`, {
                headers: { Authorization: `Bearer ${token}` }
              });
              const rawStudents = res.data?.items || res.data || [];
              studentList = rawStudents.map(item => {
                if (item.id && item.name && item.admissionNumber) {
                  return { ...item, classId: cls.classId, className: cls.name };
                }
                const userObj = item.user || {};
                const studentInfoObj = item.studentInfo || {};
                return {
                  id: userObj.userId || item.id || '',
                  name: `${userObj.firstName || ''} ${userObj.lastName || ''}`.trim() || item.name || 'Unknown Student',
                  admissionNumber: studentInfoObj.admissionId || item.admissionNumber || 'N/A',
                  active: userObj.active !== false && userObj.status !== 'Inactive',
                  status: userObj.status || 'Active',
                  remark: item.remark || '',
                  classId: cls.classId,
                  className: cls.name
                };
              });
            } catch (e) {
              console.warn(`Failed API student fetch in Reports for class ${cls.classId}`, e);
            }
          }

          if (!studentList || studentList.length === 0) {
            const rawSeed = DEFAULT_STUDENTS[cls.classId] || [];
            studentList = rawSeed.map(item => ({
              id: item.id,
              name: item.name,
              admissionNumber: item.admissionNumber,
              active: item.active !== false && item.status !== 'Inactive',
              status: item.status || 'Active',
              remark: item.remark || '',
              classId: cls.classId,
              className: cls.name
            }));
          }
          return studentList;
        });

        const studentResults = await Promise.all(studentPromises);
        const combinedStudents = studentResults.flat();
        const activeStudents = combinedStudents.filter(s => s.active && s.status !== "Inactive");

        setStudents(activeStudents);
      } catch (e) {
        console.error("Failed loading students list in reports", e);
      } finally {
        setIsLoading(false);
      }
    };

    loadStudents();
  }, [matchingClasses, token, axios]);

  // Compiled reports data metrics
  const reportData = useMemo(() => {
    if (students.length === 0) return { studentReports: [], defaulters: [], subjectReports: [], classAvg: 100, schoolAbsenteesToday: 0 };

    const allHistory = getStoredAttendance();
    const studentReports = [];
    const defaulters = [];
    
    let totalPresentSum = 0;
    let totalClassesSum = 0;
    const threshold = settings?.defaulterThreshold || 75;

    const getStatsForStudent = (studentId) => {
      // Compute stats from database records
      const records = dbRecords ? dbRecords.filter(r => r.userId === studentId) : [];
      let presentCount = 0;
      let absentCount = 0;
      let lateCount = 0;
      let leaveCount = 0;
      
      records.forEach(r => {
        const status = mapStatusFromBackend(r.attendanceStatus);
        if (status === 'Present') presentCount++;
        else if (status === 'Absent') absentCount++;
        else if (status === 'Late') lateCount++;
        else if (status === 'Leave') leaveCount++;
      });
      
      const totalClasses = records.length;
      const attendedCount = presentCount + lateCount;
      const percentage = totalClasses > 0 ? Math.round(((attendedCount + leaveCount) / totalClasses) * 100) : 100;
      
      return {
        percentage,
        totalClasses,
        presentCount,
        absentCount,
        lateCount,
        leaveCount,
        records: records.map(r => ({
          date: formatLocalDate(r.attendanceDate),
          subjectCode: r.subject || 'General',
          subjectName: r.subject || 'General',
          status: mapStatusFromBackend(r.attendanceStatus),
          remark: r.remarks || ''
        }))
      };
    };

    // 1. Process stats for each student
    students.forEach(student => {
      const stats = getStatsForStudent(student.id);
      
      // If we are filtering by a specific subject, calculate subject-specific percentage
      let finalPercentage = stats.percentage;
      let totalFiltered = stats.totalClasses;
      let presentFiltered = stats.presentCount + stats.lateCount;
      let leaveFiltered = stats.leaveCount;
      let absentFiltered = stats.absentCount;

      if (selectedSubject !== 'ALL') {
        const targetSub = subjectsList.find(sub => sub.courseCode === selectedSubject);
        const targetSubjectName = targetSub ? targetSub.courseName : selectedSubject;

        let subTotal = 0;
        let subPresent = 0;
        let subAbsent = 0;
        let subLate = 0;
        let subLeave = 0;

        stats.records.forEach(rec => {
          if (rec.subjectName === targetSubjectName || rec.subjectCode === selectedSubject) {
            subTotal++;
            if (rec.status === 'Present') subPresent++;
            else if (rec.status === 'Absent') subAbsent++;
            else if (rec.status === 'Late') subLate++;
            else if (rec.status === 'Leave') subLeave++;
          }
        });

        totalFiltered = subTotal;
        presentFiltered = subPresent + subLate;
        leaveFiltered = subLeave;
        absentFiltered = subAbsent;
        finalPercentage = subTotal > 0 ? Math.round(((presentFiltered + leaveFiltered) / subTotal) * 100) : 100;
      }

      const reportItem = {
        id: student.id,
        name: student.name,
        admissionNumber: student.admissionNumber,
        totalClasses: totalFiltered,
        present: presentFiltered,
        absent: absentFiltered,
        leave: leaveFiltered,
        percentage: finalPercentage,
        status: finalPercentage < threshold ? 'Warning' : 'Good'
      };

      studentReports.push(reportItem);
      
      if (finalPercentage < threshold && totalFiltered > 0) {
        defaulters.push(reportItem);
      }

      totalPresentSum += presentFiltered + leaveFiltered;
      totalClassesSum += totalFiltered;
    });

    // 2. Process Subject-wise breakdowns
    const subjectReports = [];
    subjectsList.forEach(sub => {
      let subTotal = 0;
      let subPresent = 0;
      let subAbsent = 0;
      let subLate = 0;
      let subLeave = 0;

      students.forEach(student => {
        const stats = getStatsForStudent(student.id);
        stats.records.forEach(rec => {
          if (rec.subjectName === sub.courseName || rec.subjectCode === sub.courseCode) {
            subTotal++;
            if (rec.status === 'Present') subPresent++;
            else if (rec.status === 'Absent') subAbsent++;
            else if (rec.status === 'Late') subLate++;
            else if (rec.status === 'Leave') subLeave++;
          }
        });
      });

      const effectivePresent = subPresent + subLate;
      const subPercentage = subTotal > 0 ? Math.round(((effectivePresent + subLeave) / subTotal) * 100) : 100;

      subjectReports.push({
        subjectCode: sub.courseCode,
        subjectName: sub.courseName,
        periodsHeld: subTotal,
        absences: subAbsent,
        percentage: subPercentage
      });
    });

    // 3. School absences today
    let schoolAbsenteesToday = 0;
    const todayStr = new Date().toISOString().split('T')[0];
    
    if (dbRecords && dbRecords.length > 0) {
      dbRecords.forEach(r => {
        if (r.attendanceDate === todayStr && r.attendanceStatus === 'Absent') {
          schoolAbsenteesToday++;
        }
      });
    } else {
      Object.entries(allHistory).forEach(([key, record]) => {
        if (record.date === todayStr) {
          record.students.forEach(st => {
            if (st.status === 'Absent') schoolAbsenteesToday++;
          });
        }
      });
    }

    const classAvg = totalClassesSum > 0 ? Math.round((totalPresentSum / totalClassesSum) * 100) : 100;

    return {
      studentReports,
      defaulters,
      subjectReports,
      classAvg,
      schoolAbsenteesToday
    };
  }, [students, selectedCenter, selectedClassId, selectedSubject, startDate, endDate, settings, subjectsList, dbRecords, isCustomOrg]);

  // Handle "Notify Parent" warning simulation
  const handleNotifyParent = (student) => {
    setNotifiedStudents(prev => ({ ...prev, [student.id]: true }));
    toast.success(`Warning alert SMS/Email successfully transmitted to parent of ${student.name}.`);
  };

  // Search filter
  const filteredStudentReports = useMemo(() => {
    return reportData.studentReports.filter(r =>
      r.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.admissionNumber.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [reportData.studentReports, searchQuery]);

  // Export to CSV Function
  const handleExportCSV = () => {
    let csvContent = "data:text/csv;charset=utf-8,";
    const className = isCustomOrg ? selectedCenter : (classesList.find(c => c.classId === selectedClassId)?.name || 'Class');
    
    if (activeTab === 'defaulters') {
      csvContent += `Defaulter Students Report - ${isCustomOrg ? 'Center' : 'Class'} ${className} (${startDate} to ${endDate})\n`;
      csvContent += "Roll No,Student Name,Admission No,Total Classes,Absent Count,Percentage%\n";
      reportData.defaulters.forEach((r, idx) => {
        csvContent += `"${idx + 1}","${r.name}","${r.admissionNumber}","${r.totalClasses}","${r.absent}","${r.percentage}%"\n`;
      });
    } else if (activeTab === 'subjectWise') {
      csvContent += `Subject Breakdown Report - ${isCustomOrg ? 'Center' : 'Class'} ${className} (${startDate} to ${endDate})\n`;
      csvContent += "Subject Code,Subject Name,Periods Held,Absences,Attendance%\n";
      reportData.subjectReports.forEach((r) => {
        csvContent += `"${r.subjectCode}","${r.subjectName}","${r.periodsHeld}","${r.absences}","${r.percentage}%"\n`;
      });
    } else {
      csvContent += `Student Attendance Summary - ${isCustomOrg ? 'Center' : 'Class'} ${className} (${startDate} to ${endDate})\n`;
      csvContent += "Roll No,Student Name,Admission No,Total Classes,Present,Absent,Leave,Percentage%,Status\n";
      reportData.studentReports.forEach((r, idx) => {
        csvContent += `"${idx + 1}","${r.name}","${r.admissionNumber}","${r.totalClasses}","${r.present}","${r.absent}","${r.leave}","${r.percentage}%","${r.status}"\n`;
      });
    }

    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `Attendance_Report_${activeTab}_${isCustomOrg ? selectedCenter : selectedClassId}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("CSV file downloaded successfully.");
  };

  // Export to PDF using jsPDF + AutoTable
  const handleExportPDF = () => {
    try {
      const doc = new jsPDF();
      const className = isCustomOrg ? selectedCenter : (classesList.find(c => c.classId === selectedClassId)?.name || 'Class');
      
      // Set Document Header
      doc.setFontSize(20);
      doc.setTextColor(30, 41, 59); // Slate 800
      doc.text("eGurukul School ERP - Attendance Report", 14, 20);
      
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139); // Slate 500
      doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 26);
      doc.text(`Date Range: ${startDate} to ${endDate}`, 14, 32);
      doc.text(`${isCustomOrg ? 'Center Filter' : 'Class Filter'}: ${className}`, 14, 38);
      
      // Draw Divider Line
      doc.setDrawColor(226, 232, 240);
      doc.line(14, 42, 196, 42);

      let tableColumn = [];
      let tableRows = [];
      let reportTitle = "";

      if (activeTab === 'defaulters') {
        reportTitle = `DEFAULTER STUDENTS LIST (Threshold < ${settings?.defaulterThreshold || 75}%)`;
        tableColumn = ["Roll No", "Student Name", "Admission No", "Total Classes", "Absent", "Attendance %"];
        reportData.defaulters.forEach((r, idx) => {
          tableRows.push([idx + 1, r.name, r.admissionNumber, r.totalClasses, r.absent, `${r.percentage}%`]);
        });
      } else if (activeTab === 'subjectWise') {
        reportTitle = "SUBJECT BREAKDOWNS";
        tableColumn = ["Subject Code", "Subject Name", "Periods Held", "Absences", "Attendance %"];
        reportData.subjectReports.forEach((r) => {
          tableRows.push([r.subjectCode, r.subjectName, r.periodsHeld, r.absences, `${r.percentage}%`]);
        });
      } else {
        reportTitle = "STUDENT ATTENDANCE SUMMARY MATRIX";
        tableColumn = ["Roll No", "Student Name", "Admission No", "Total Classes", "Present", "Absent", "Leave", "Attendance %"];
        reportData.studentReports.forEach((r, idx) => {
          tableRows.push([idx + 1, r.name, r.admissionNumber, r.totalClasses, r.present, r.absent, r.leave, `${r.percentage}%`]);
        });
      }

      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.setTextColor(30, 41, 59);
      doc.text(reportTitle, 14, 50);

      doc.autoTable({
        startY: 55,
        head: [tableColumn],
        body: tableRows,
        theme: 'striped',
        headStyles: { fillColor: [59, 130, 246] }, // primary blue
        styles: { fontSize: 9 },
        margin: { top: 30 }
      });

      doc.save(`Attendance_Report_${activeTab}_${isCustomOrg ? selectedCenter : selectedClassId}.pdf`);
      toast.success("High-fidelity PDF document exported successfully.");
    } catch (e) {
      console.error("PDF construction crashed", e);
      toast.error("Failed to generate PDF. Falling back to browser print.");
      window.print();
    }
  };

  return (
    <div className="reports-container">
      <ToastContainer position="top-right" autoClose={3000} />

      <div className="reports-header">
        <div>
          <h1>Attendance Analytics</h1>
          <p>Obtain analytical reports, defaulter listings, and document exports.</p>
        </div>
        <span className="role-badge">{userRole} access</span>
      </div>

      {/* Analytics Widgets Row */}
      <div className="reports-stats-grid">
        <div className="reports-stat-card">
          <div className="reports-stat-content">
            <h3>{reportData.classAvg}%</h3>
            <p>{isCustomOrg ? "CENTER ATTENDANCE AVERAGE" : "CLASS ATTENDANCE AVERAGE"}</p>
          </div>
          <div className="reports-stat-badge success"><AssessmentIcon /></div>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-content">
            <h3>{reportData.defaulters.length}</h3>
            <p>DEFAULTER STUDENTS</p>
          </div>
          <div className="reports-stat-badge critical"><WarningAmberIcon /></div>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-content">
            <h3>{reportData.schoolAbsenteesToday}</h3>
            <p>{isCustomOrg ? "TOTAL ABSENT CENTER-WIDE TODAY" : "TOTAL ABSENT CLASS-WIDE TODAY"}</p>
          </div>
          <div className="reports-stat-badge primary"><ClassIcon /></div>
        </div>

        <div className="reports-stat-card">
          <div className="reports-stat-content">
            <h3>{settings?.defaulterThreshold || 75}%</h3>
            <p>DEFAULTER BAR THRESHOLD</p>
          </div>
          <div className="reports-stat-badge warning"><SchoolIcon /></div>
        </div>
      </div>

      {/* Filters Card */}
      <div className="report-filters-card">
        <div className="report-filters-grid">
          <div className="filter-group">
            <label>Start Date</label>
            <input
              type="date"
              className="filter-control"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>End Date</label>
            <input
              type="date"
              className="filter-control"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>

          <div className="filter-group">
            <label>{isCustomOrg ? "KV Center" : "Class"}</label>
            {isCustomOrg ? (
              <select
                className="filter-control"
                value={selectedCenter}
                onChange={(e) => setSelectedCenter(e.target.value)}
              >
                {uniqueCenters.map(center => (
                  <option key={center} value={center}>{center}</option>
                ))}
              </select>
            ) : (
              <select
                className="filter-control"
                value={selectedClassId}
                onChange={(e) => setSelectedClassId(e.target.value)}
              >
                {classesList.map(cls => (
                  <option key={cls.classId} value={cls.classId}>{cls.name}</option>
                ))}
              </select>
            )}
          </div>

          <div className="filter-group">
            <label>Subject Filter</label>
            <select
              className="filter-control"
              value={selectedSubject}
              onChange={(e) => setSelectedSubject(e.target.value)}
            >
              <option value="ALL">All Subjects</option>
              {subjectsList.map(s => (
                <option key={s.courseCode} value={s.courseCode}>{s.courseName}</option>
              ))}
            </select>
          </div>

          <div className="action-export-group">
            <button className="btn-export-csv" onClick={handleExportCSV}>
              <CloudDownloadIcon style={{ fontSize: '16px' }} /> Excel/CSV
            </button>
            <button className="btn-export-pdf" onClick={handleExportPDF}>
              <CloudDownloadIcon style={{ fontSize: '16px' }} /> PDF Document
            </button>
          </div>
        </div>
      </div>

      {/* Tabs list bar */}
      <div className="reports-tabs-bar">
        <button
          className={`reports-tab-btn ${activeTab === 'defaulters' ? 'active' : ''}`}
          onClick={() => { setActiveTab('defaulters'); setSearchQuery(''); }}
        >
          Defaulters List ({reportData.defaulters.length})
        </button>
        <button
          className={`reports-tab-btn ${activeTab === 'subjectWise' ? 'active' : ''}`}
          onClick={() => { setActiveTab('subjectWise'); setSearchQuery(''); }}
        >
          Subject Breakdown
        </button>
        <button
          className={`reports-tab-btn ${activeTab === 'studentSummary' ? 'active' : ''}`}
          onClick={() => { setActiveTab('studentSummary'); setSearchQuery(''); }}
        >
          {isCustomOrg ? "Complete Center Summary" : "Complete Class Summary"}
        </button>
      </div>

      {/* Result Cards based on tabs */}
      <div className="report-result-card">
        {isLoading ? (
          <div className="loading-container">
            <div className="spinner"></div>
            <p>Compiling database analytical aggregates...</p>
          </div>
        ) : (
          <>
            {activeTab === 'defaulters' && (
              <>
                <div className="report-card-heading">
                  <div>
                    <h2>Defaulter Roster</h2>
                    <p>List of students falling below the minimum threshold percentage limit (&lt;{settings?.defaulterThreshold || 75}%).</p>
                  </div>
                </div>

                {reportData.defaulters.length === 0 ? (
                  <div className="empty-container">
                    <AssessmentIcon />
                    <h3>No Defaulters Found!</h3>
                    <p>All active students have overall attendance within the desired parameters.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="roster-table">
                      <thead>
                        <tr>
                          <th>Roll</th>
                          <th>Student Name</th>
                          <th>Admission No</th>
                          <th>Total Periods</th>
                          <th>Absent Periods</th>
                          <th>Attendance %</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reportData.defaulters.map((item, idx) => (
                          <tr key={item.id} className="warning-row">
                            <td style={{ fontWeight: 600 }}>{idx + 1}</td>
                            <td style={{ fontWeight: 600, color: '#1e293b' }}>{item.name}</td>
                            <td>{item.admissionNumber}</td>
                            <td>{item.totalClasses}</td>
                            <td style={{ color: '#ef4444', fontWeight: 600 }}>{item.absent}</td>
                            <td className="warning-percentage">{item.percentage}%</td>
                            <td>
                              <button
                                className={`btn-notify-parent ${notifiedStudents[item.id] ? 'sent' : ''}`}
                                disabled={notifiedStudents[item.id]}
                                onClick={() => handleNotifyParent(item)}
                              >
                                <NotificationsActiveIcon style={{ fontSize: '13px', marginRight: '4px', verticalAlign: 'middle' }} />
                                {notifiedStudents[item.id] ? 'Alert Sent' : 'Notify Parent'}
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}

            {activeTab === 'subjectWise' && (
              <>
                <div className="report-card-heading">
                  <div>
                    <h2>Subject Breakdown Analysis</h2>
                    <p>Attendance percentage overview mapped across subjects and course structures.</p>
                  </div>
                </div>

                <div className="table-responsive">
                  <table className="roster-table">
                    <thead>
                      <tr>
                        <th>Subject Code</th>
                        <th>Subject Name</th>
                        <th>Periods Held</th>
                        <th>Class Absences</th>
                        <th>Class Avg Attendance %</th>
                        <th style={{ width: '220px' }}>Attendance Bar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {reportData.subjectReports.map((item) => {
                        const isUnder = item.percentage < (settings?.defaulterThreshold || 75);
                        let barColor = 'success';
                        if (item.percentage < 60) barColor = 'danger';
                        else if (isUnder) barColor = 'warning';

                        return (
                          <tr key={item.subjectCode}>
                            <td style={{ fontWeight: 600, color: '#64748b' }}>{item.subjectCode}</td>
                            <td style={{ fontWeight: 600, color: '#1e293b' }}>{item.subjectName}</td>
                            <td>{item.periodsHeld}</td>
                            <td style={{ color: '#ef4444', fontWeight: 600 }}>{item.absences}</td>
                            <td className={isUnder ? 'warning-percentage' : 'normal-percentage'}>
                              {item.percentage}%
                            </td>
                            <td>
                              <div className="progress-bar-container">
                                <div
                                  className={`progress-bar-fill ${barColor}`}
                                  style={{ width: `${item.percentage}%` }}
                                ></div>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </>
            )}

            {activeTab === 'studentSummary' && (
              <>
                <div className="report-card-heading">
                  <div>
                    <h2>{isCustomOrg ? "Complete Center Ledger Summary" : "Complete Class Ledger Summary"}</h2>
                    <p>{isCustomOrg ? "Total aggregates, absences, and parameters compiled for active roster." : "Total aggregates, absences, and parameters compiled for active class."}</p>
                  </div>
                  <div className="search-box">
                    <span className="search-icon-pos"><SearchIcon /></span>
                    <input
                      type="text"
                      placeholder="Search student summary..."
                      className="search-input"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                </div>

                {filteredStudentReports.length === 0 ? (
                  <div className="empty-container">
                    <SearchIcon />
                    <h3>No Records Matched</h3>
                    <p>Try matching student name or registration codes again.</p>
                  </div>
                ) : (
                  <div className="table-responsive">
                    <table className="roster-table">
                      <thead>
                        <tr>
                          <th>Roll</th>
                          <th>Student Name</th>
                          <th>Admission No</th>
                          <th>Total Days</th>
                          <th>Present</th>
                          <th>Absent</th>
                          <th>Leave</th>
                          <th>Attendance %</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredStudentReports.map((item, idx) => {
                          const isWarning = item.status === 'Warning';
                          return (
                            <tr key={item.id} className={isWarning ? 'warning-row' : ''}>
                              <td>{idx + 1}</td>
                              <td style={{ fontWeight: 600, color: '#1e293b' }}>{item.name}</td>
                              <td>{item.admissionNumber}</td>
                              <td>{item.totalClasses}</td>
                              <td style={{ color: '#10b981', fontWeight: 600 }}>{item.present}</td>
                              <td style={{ color: '#ef4444', fontWeight: 600 }}>{item.absent}</td>
                              <td style={{ color: '#3b82f6', fontWeight: 600 }}>{item.leave}</td>
                              <td className={isWarning ? 'warning-percentage' : 'normal-percentage'}>
                                {item.percentage}%
                              </td>
                              <td>
                                <span
                                  className="stat-pill"
                                  style={{
                                    backgroundColor: isWarning ? '#fee2e2' : '#d1fae5',
                                    color: isWarning ? '#b91c1c' : '#065f46',
                                    padding: '4px 8px',
                                    borderRadius: '4px',
                                    fontSize: '11px',
                                    fontWeight: 700
                                  }}
                                >
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default AttendanceReports;
